/* First Capitol Cards storefront usability enhancements */
(() => {
  'use strict';

  const ready = (callback) => {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', callback, { once: true });
    else callback();
  };

  const root = () => {
    const value = window.Shopify?.routes?.root || '/';
    return value.endsWith('/') ? value : `${value}/`;
  };

  const handleize = (value) => String(value || '')
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
    .replace(/&/g, ' and ').replace(/[’']/g, '')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');

  const debounce = (callback, delay = 220) => {
    let timer;
    return (...args) => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => callback(...args), delay);
    };
  };

  const money = new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
    style: 'currency',
    currency: window.Shopify?.currency?.active || 'USD'
  });

  const initializePredictiveSearch = () => {
    document.querySelectorAll('.header-search').forEach((form) => {
      const input = form.querySelector('input[type="search"]');
      if (!input || form.dataset.fccPredictiveReady) return;
      form.dataset.fccPredictiveReady = 'true';
      form.classList.add('fcc-predictive-search');
      input.autocomplete = 'off';
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-expanded', 'false');

      const panel = document.createElement('div');
      panel.className = 'fcc-predictive-search__panel';
      panel.id = `fcc-predictive-${Math.random().toString(36).slice(2, 9)}`;
      panel.setAttribute('role', 'listbox');
      panel.hidden = true;
      input.setAttribute('aria-controls', panel.id);
      form.appendChild(panel);

      let controller;
      let searchVersion = 0;
      let activeIndex = -1;
      const close = () => {
        searchVersion += 1;
        controller?.abort();
        panel.hidden = true;
        panel.replaceChildren();
        activeIndex = -1;
        input.setAttribute('aria-expanded', 'false');
        input.removeAttribute('aria-activedescendant');
      };
      const status = (text) => {
        const message = document.createElement('p');
        message.className = 'fcc-predictive-search__status';
        message.setAttribute('role', 'status');
        message.textContent = text;
        panel.replaceChildren(message);
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      };
      const imageUrl = (product) => {
        if (!product?.image) return '';
        return typeof product.image === 'string' ? product.image : product.image.url || product.image.src || '';
      };
      const render = (products, query) => {
        panel.replaceChildren();
        activeIndex = -1;
        if (!products.length) {
          status(`No available products found for “${query}”.`);
          return;
        }

        products.forEach((product, index) => {
          const link = document.createElement('a');
          link.className = 'fcc-predictive-search__item';
          link.href = product.url || `${root()}search?q=${encodeURIComponent(query)}&type=product`;
          link.id = `${panel.id}-option-${index}`;
          link.setAttribute('role', 'option');
          link.setAttribute('aria-selected', 'false');

          const source = imageUrl(product);
          if (source) {
            const image = document.createElement('img');
            image.className = 'fcc-predictive-search__image';
            image.src = source;
            image.alt = product.image?.alt || product.title || '';
            image.loading = 'lazy';
            link.appendChild(image);
          } else {
            const placeholder = document.createElement('span');
            placeholder.className = 'fcc-predictive-search__image fcc-predictive-search__image--placeholder';
            placeholder.textContent = 'FCC';
            placeholder.setAttribute('aria-hidden', 'true');
            link.appendChild(placeholder);
          }

          const copy = document.createElement('span');
          copy.className = 'fcc-predictive-search__copy';
          const title = document.createElement('span');
          title.className = 'fcc-predictive-search__title';
          title.textContent = String(product.title || '').replace(/\s+-\s+Manual$/i, '');
          const type = document.createElement('span');
          type.className = 'fcc-predictive-search__type';
          type.textContent = product.type || product.vendor || 'Product';
          copy.append(title, type);
          link.appendChild(copy);
          panel.appendChild(link);
        });

        const all = document.createElement('a');
        all.className = 'fcc-predictive-search__all';
        all.href = `${root()}search?q=${encodeURIComponent(query)}&type=product&options%5Bunavailable_products%5D=hide`;
        all.textContent = `View all results for “${query}”`;
        panel.appendChild(all);
        panel.hidden = false;
        input.setAttribute('aria-expanded', 'true');
      };

      const search = debounce(async (version) => {
        if (version !== searchVersion) return;
        const query = input.value.trim();
        if (query.length < 2) return close();
        controller?.abort();
        controller = new AbortController();
        status('Searching current inventory…');
        const params = new URLSearchParams({ q: query });
        params.set('resources[type]', 'product');
        params.set('resources[limit]', '6');
        params.set('resources[options][unavailable_products]', 'hide');
        try {
          const response = await fetch(`${root()}search/suggest.json?${params}`, {
            credentials: 'same-origin',
            headers: { Accept: 'application/json' },
            signal: controller.signal
          });
          if (!response.ok) throw new Error(String(response.status));
          const data = await response.json();
          if (version !== searchVersion || input.value.trim() !== query) return;
          render(data?.resources?.results?.products || [], query);
        } catch (error) {
          if (error.name !== 'AbortError' && version === searchVersion) close();
        }
      });

      input.addEventListener('input', () => {
        close();
        if (input.value.trim().length >= 2) search(searchVersion);
      });
      input.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          event.preventDefault();
          return close();
        }
        if (panel.hidden) return;
        const options = [...panel.querySelectorAll('.fcc-predictive-search__item')];
        if (!options.length) return;
        if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
          event.preventDefault();
          activeIndex = (activeIndex + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
          options.forEach((option, index) => {
            const active = index === activeIndex;
            option.classList.toggle('is-focused', active);
            option.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          input.setAttribute('aria-activedescendant', options[activeIndex].id);
          options[activeIndex].scrollIntoView({ block: 'nearest' });
        } else if (event.key === 'Enter' && activeIndex >= 0) {
          event.preventDefault();
          options[activeIndex].click();
        }
      });
      document.addEventListener('click', (event) => {
        if (!form.contains(event.target)) close();
      });
      form.addEventListener('focusout', (event) => {
        if (!form.contains(event.relatedTarget)) close();
      });
    });
  };

  const pathTags = () => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    const collectionIndex = parts.indexOf('collections');
    return collectionIndex >= 0 && parts[collectionIndex + 2]
      ? parts[collectionIndex + 2].split('+').filter(Boolean)
      : [];
  };
  const collectionBase = () => {
    const parts = window.location.pathname.split('/').filter(Boolean);
    const collectionIndex = parts.indexOf('collections');
    const handle = collectionIndex >= 0 ? parts[collectionIndex + 1] : 'all';
    return `${root()}collections/${handle || 'all'}`.replace(/\/$/, '');
  };
  const department = () => {
    const tags = pathTags();
    const catalog = `${root()}collections/all`.replace(/\/$/, '');
    const has = (...values) => values.some((value) => tags.includes(value));
    if (has('pokemon', 'game-pokemon')) return { key: 'pokemon', label: 'Pokémon', base: `${catalog}/pokemon` };
    if (has('magic', 'game-magic')) return { key: 'magic', label: 'Magic', base: `${catalog}/magic` };
    if (has('one-piece', 'game-one-piece')) return { key: 'one-piece', label: 'One Piece', base: `${catalog}/one-piece` };
    if (has('sports', 'game-sports', 'sports-cards', 'game-sports-cards')) return { key: 'sports', label: 'Sports cards', base: `${catalog}/sports` };
    if (has('board-games', 'department-board-games')) return { key: 'board-games', label: 'Board games', base: `${catalog}/board-games` };
    if (has('accessories', 'department-accessories')) return { key: 'accessories', label: 'Accessories', base: `${catalog}/accessories` };
    return null;
  };
  const removeTagUrl = (tagText) => {
    const tags = pathTags();
    const index = tags.indexOf(handleize(tagText));
    if (index < 0) return '';
    tags.splice(index, 1);
    const query = window.location.search;
    return `${tags.length ? `${collectionBase()}/${tags.join('+')}` : collectionBase()}${query}`;
  };

  const initializeCollectionFilters = () => {
    const filters = document.querySelector('.main-collection .filters--curated-only');
    if (!filters) return;
    const currentDepartment = department();

    const shortcuts = document.createElement('nav');
    shortcuts.className = 'fcc-filter-shortcuts';
    shortcuts.setAttribute('aria-label', currentDepartment ? `${currentDepartment.label} product types` : 'Shop departments');
    const shortcutLabel = document.createElement('p');
    shortcutLabel.className = 'fcc-filter-shortcuts__label';
    shortcutLabel.textContent = currentDepartment && ['pokemon', 'magic', 'one-piece', 'sports'].includes(currentDepartment.key)
      ? 'Product type' : 'Shop by department';
    shortcuts.appendChild(shortcutLabel);
    const catalog = `${root()}collections/all`.replace(/\/$/, '');
    const definitions = currentDepartment && ['pokemon', 'magic', 'one-piece', 'sports'].includes(currentDepartment.key)
      ? [['All', currentDepartment.base], ['Singles', `${currentDepartment.base}+single`], ['Graded', `${currentDepartment.base}+graded`], ['Sealed', `${currentDepartment.base}+sealed`]]
      : [['Pokémon', `${catalog}/pokemon`], ['Magic', `${catalog}/magic`], ['One Piece', `${catalog}/one-piece`], ['Sports cards', `${catalog}/sports`], ['Board games', `${catalog}/board-games`], ['Accessories', `${catalog}/accessories`]];
    const currentPath = window.location.pathname.replace(/\/$/, '');
    definitions.forEach(([label, href]) => {
      const link = document.createElement('a');
      link.href = href;
      link.textContent = label;
      if (currentPath === href) {
        link.classList.add('is-active');
        link.setAttribute('aria-current', 'page');
      }
      shortcuts.appendChild(link);
    });
    (filters.querySelector('.filters__note') || filters.firstElementChild).insertAdjacentElement('afterend', shortcuts);

    filters.querySelectorAll('[data-filter-group]').forEach((group) => {
      const search = group.querySelector('[data-filter-option-search]');
      const options = [...group.querySelectorAll('[data-filter-option]')];
      const empty = group.querySelector('[data-filter-empty]');
      if (!search || (!options.length && !group.hasAttribute('data-department-scope'))) return;
      search.addEventListener('input', () => {
        const query = search.value.trim().toLocaleLowerCase();
        let visible = 0;
        group.querySelectorAll('[data-filter-option]').forEach((option) => {
          const value = (option.dataset.filterValue || option.textContent).toLocaleLowerCase();
          const belongs = !group.hasAttribute('data-department-scope') || option.hasAttribute('data-scope-match');
          const show = belongs && (!query || value.includes(query));
          option.hidden = !show;
          if (show) visible += 1;
        });
        if (empty) empty.hidden = visible > 0 || group.getAttribute('aria-busy') === 'true';
      });
    });

    initializeSetPickers(filters);

    const currentTags = document.querySelector('.collection-current-tags');
    if (!currentTags) return;
    const labels = [...currentTags.querySelectorAll('span')].map((tag) => tag.textContent.trim()).filter(Boolean);
    if (!labels.length) return;
    currentTags.classList.add('fcc-active-filters');
    currentTags.replaceChildren();
    const showing = document.createElement('strong');
    showing.textContent = 'Showing';
    currentTags.appendChild(showing);
    const fixed = new Set(['pokemon', 'game-pokemon', 'magic', 'game-magic', 'one-piece', 'game-one-piece', 'sports', 'game-sports', 'sports-cards', 'game-sports-cards', 'board-games', 'department-board-games', 'accessories', 'department-accessories']);
    labels.forEach((label) => {
      const url = fixed.has(handleize(label)) ? '' : removeTagUrl(label);
      const chip = document.createElement(url ? 'a' : 'span');
      chip.textContent = label;
      if (url) {
        chip.href = url;
        chip.setAttribute('aria-label', `Remove ${label} filter`);
      }
      currentTags.appendChild(chip);
    });
    const clear = filters.querySelector('.filters__header a');
    const hasOptionalFilters = labels.some((label) => label.includes(':') || label.includes('：'));
    if (clear && hasOptionalFilters) {
      const clearOptional = clear.cloneNode(true);
      clearOptional.className = 'fcc-clear-optional-filters';
      clearOptional.textContent = 'Clear optional filters';
      currentTags.appendChild(clearOptional);
    }
  };

  const initializeProductMedia = () => {
    document.querySelectorAll('.product-media__thumb').forEach((thumb, index) => {
      if (index === 0) {
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-pressed', 'true');
      }
      const activate = () => {
        const media = thumb.closest('.product-media');
        const main = media?.querySelector('.product-media__main img');
        const image = thumb.querySelector('img');
        if (!main || !image) return;
        main.src = thumb.dataset.fullImage || image.currentSrc || image.src;
        main.removeAttribute('srcset');
        main.alt = image.alt;
        media.querySelectorAll('.product-media__thumb').forEach((button) => {
          button.classList.remove('is-active');
          button.setAttribute('aria-pressed', 'false');
        });
        thumb.classList.add('is-active');
        thumb.setAttribute('aria-pressed', 'true');
      };
      thumb.addEventListener('click', activate);
    });
  };

  const renderPrice = (container, price, compareAt) => {
    if (!container) return;
    const current = Number(price) || 0;
    const previous = Number(compareAt) || 0;
    const wrap = document.createElement('div');
    wrap.className = previous > current ? 'price price--sale' : 'price';
    const amount = document.createElement('span');
    amount.textContent = money.format(current / 100);
    wrap.appendChild(amount);
    if (previous > current) {
      const compare = document.createElement('s');
      compare.textContent = money.format(previous / 100);
      wrap.appendChild(compare);
    }
    container.replaceChildren(wrap);
  };

  const initializeVariants = () => {
    document.querySelectorAll('.product-form select[name="id"]').forEach((select) => {
      const form = select.closest('form');
      const section = select.closest('.main-product');
      const button = form?.querySelector('button[type="submit"]');
      const desktopPrice = select.closest('.product-info')?.querySelector('[data-product-price]');
      const mobilePrice = section?.querySelector('[data-mobile-product-price]');
      select.addEventListener('change', () => {
        const option = select.selectedOptions[0];
        if (!option) return;
        const available = option.dataset.available !== 'false';
        renderPrice(desktopPrice, option.dataset.price, option.dataset.compareAtPrice);
        renderPrice(mobilePrice, option.dataset.price, option.dataset.compareAtPrice);
        if (button) {
          button.disabled = !available;
          button.textContent = available ? 'Add to cart' : 'Unavailable';
        }
        if (option.dataset.image) {
          const main = section?.querySelector('.product-media__main img');
          if (main) {
            main.src = option.dataset.image;
            main.removeAttribute('srcset');
          }
        }
        const url = new URL(window.location.href);
        url.searchParams.set('variant', option.value);
        window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
      });
    });
  };

  const initializeAddresses = () => {
    document.querySelectorAll('select[data-default]').forEach((select) => {
      if (select.dataset.default) select.value = select.dataset.default;
    });

    document.querySelectorAll('[data-address-delete]').forEach((button) => {
      button.addEventListener('click', () => {
        if (!window.confirm(button.dataset.confirm || 'Delete this address?')) return;
        const target = button.dataset.addressDelete;
        if (!target) return;
        if (window.Shopify?.postLink) {
          window.Shopify.postLink(target, { parameters: { _method: 'delete' } });
          return;
        }
        const form = document.createElement('form');
        form.method = 'post';
        form.action = target;
        const method = document.createElement('input');
        method.type = 'hidden';
        method.name = '_method';
        method.value = 'delete';
        form.appendChild(method);
        document.body.appendChild(form);
        form.submit();
      });
    });

  };

  const initializeSetPickers = (filters) => {
    const setPickers = [...filters.querySelectorAll('[data-set-picker]')];
    setPickers.forEach((picker) => {
      const search = picker.querySelector('[data-filter-option-search]');
      const summary = picker.querySelector('summary');
      summary.addEventListener('click', () => {
        if (!picker.open) setPickers.forEach((other) => { if (other !== picker) other.open = false; });
      });
      picker.addEventListener('toggle', () => {
        if (picker.open && document.activeElement === summary) search.focus({ preventScroll: true });
      });
      picker.addEventListener('keydown', (event) => {
        if (!picker.open) return;
        if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          picker.open = false;
          summary.focus({ preventScroll: true });
          return;
        }
        if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
        const links = [...picker.querySelectorAll('[data-filter-option]:not([hidden]) .tag-filter__link:not([aria-disabled="true"])')]
          .filter((link) => !picker.hasAttribute('data-department-scope') || link.closest('[data-scope-match]'));
        const index = links.indexOf(document.activeElement);
        if (document.activeElement !== search && index < 0) return;
        event.preventDefault();
        if (event.key === 'ArrowUp' && index <= 0) search.focus({ preventScroll: true });
        else links[Math.min(links.length - 1, index + (event.key === 'ArrowDown' ? 1 : -1))]?.focus();
      });
    });
    document.addEventListener('click', (event) => {
      setPickers.forEach((picker) => { if (!picker.contains(event.target)) picker.open = false; });
    });
  };

  const initializeRandomGameCards = () => {
    const targets = [...document.querySelectorAll('[data-random-card-pool]')];
    if (!targets.length) return;

    const gameLabels = {
      pokemon: 'Pokémon',
      magic: 'Magic',
      'one-piece': 'One Piece',
      sports: 'Sports'
    };
    const randomIndex = (length) => {
      if (window.crypto?.getRandomValues) {
        const value = new Uint32Array(1);
        window.crypto.getRandomValues(value);
        return value[0] % length;
      }
      return Math.floor(Math.random() * length);
    };
    const requests = new Map();
    const fetchPool = async (url) => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 12000);
      try {
        const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error('Card pool unavailable');
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        return JSON.parse(page.querySelector('[data-random-card-pool-data]')?.textContent || '{"products":[]}');
      } finally { clearTimeout(timer); }
    };

    targets.forEach((target) => {
      const poolUrl = target.dataset.randomCardPool;
      if (!poolUrl) return;
      if (!requests.has(poolUrl)) {
        requests.set(poolUrl, fetchPool(poolUrl).then(async (data) => {
          const fallback = Array.isArray(data.products) ? data.products : [];
          const page = randomIndex(Math.max(1, Math.min(Number(data.pages) || 1, 500))) + 1;
          if (page === 1) return fallback;
          const url = new URL(poolUrl, location.origin);
          url.searchParams.set('page', page);
          try {
            const sample = await fetchPool(url);
            return sample.products?.length ? sample.products : fallback;
          } catch (_) { return fallback; }
        }));
      }

      requests.get(poolUrl).then((products) => {
        const candidates = products.filter((product) => product?.available && product?.image && product?.url);
        if (!candidates.length) return;
        const product = candidates[randomIndex(candidates.length)];
        const game = target.dataset.heroGameCard || target.dataset.randomGameCard || '';
        const label = gameLabels[game] || game;
        const image = target.querySelector('img');
        if (image) {
          image.src = product.image;
          image.removeAttribute('srcset');
          image.alt = `${label} card: ${product.title}`;
          image.classList.remove('collection-tile__generic-image');
          image.classList.add(target.matches('[data-hero-game-card]') ? 'hero-product__card-image' : 'collection-tile__random-card-image');
        }

        const title = target.querySelector('[data-hero-product-title]');
        if (title) {
          title.textContent = String(product.title).replace(/\s+-\s+Manual$/i, '');
          target.href = product.url;
        }

        const caption = target.querySelector('.collection-tile__featured-card');
        if (caption) {
          caption.textContent = `Fresh pick: ${String(product.title).replace(/\s+-\s+Manual$/i, '')}`;
          caption.hidden = false;
        }
      }).catch(() => {});
    });
  };

  const initializeFreshFinds = () => {
    document.querySelectorAll('[data-fresh-finds-url]').forEach(async (grid) => {
      grid.dataset.freshFindsStatus = 'loading';
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), 15000);
      try {
        const response = await fetch(grid.dataset.freshFindsUrl, { credentials: 'same-origin', signal: controller.signal });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const page = new DOMParser().parseFromString(await response.text(), 'text/html');
        const seen = new Set();
        const cards = [...page.querySelectorAll('[data-fresh-finds-response] .product-card')].filter((card) => {
          const title = card.querySelector('h3')?.textContent.trim();
          if (seen.has(title)) return false;
          seen.add(title);
          return true;
        }).slice(0, Number(grid.dataset.limit) || 8);
        if (!cards.length) throw new Error('No fresh cards');
        grid.replaceChildren(...cards);
        grid.dataset.freshFindsStatus = 'ready';
        grid.closest('.featured-products').querySelector('.empty-state')?.remove();
      } catch (_) {
        grid.dataset.freshFindsStatus = 'fallback';
        // Keep the server-rendered selection if the refresh is unavailable.
      } finally { clearTimeout(timer); }
    });
  };

  ready(() => {
    initializePredictiveSearch();
    initializeCollectionFilters();
    initializeProductMedia();
    initializeVariants();
    initializeAddresses();
    initializeRandomGameCards();
    initializeFreshFinds();
  });
})();
